import type { Ref } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type {
  NotificationGroup,
  NotificationLayoutItemChip,
} from '@frameio/graphql-client/generated';
import { getThumbMedia } from '@frameio/shared/finder/utils/getThumbMedia';
import { DPR, preloadImage } from '@frameio/utils';
import { getThumbSize } from '@frameio/utils/thumbnails';
import { CHIP_AVATAR_SIZE } from '../components/NotificationCell/Body/Chip/Chip';
import { getAvatarResizedURL, getAvatarSize } from '../utils';

type PreloadImages = {
  notifications: NotificationGroup[];
};

/** On load, the notifications list can render up to 15 cells so we only need to
 * grab the avatars from the first 8 NotificationGroups to improve the perceived
 * performance */
const MAX_CELLS_TO_PRELOAD = 15;

/**
 * @description `usePreloadImages` - a hook to preload avatars, chips avatars and
 * thumbs from NotificationGroups. Only re-runs if the first notification id changes.
 *
 * @param {NotificationGroup[]} notifications - list of NotificationGroups.
 *
 * @returns void
 **/
export function usePreloadImages({ notifications }: PreloadImages) {
  const firstNotificationId = notifications?.[0]?.id;

  const imagesToPreload: Ref<Record<string, HTMLImageElement>> = useRef({});

  const getImagesToPreload = useCallback(() => {
    return notifications
      ?.slice(0, MAX_CELLS_TO_PRELOAD)
      .map((notification) => {
        const requestedAvatarSize =
          getAvatarSize(notification.content.avatars) * DPR;
        const avatarsUrls = notification.content.avatars.reduce(
          (acc: string[], avatar) => {
            if (avatar.url) {
              acc.push(getAvatarResizedURL(avatar.url, requestedAvatarSize));
            }
            return acc;
          },
          []
        );

        const thumbs = notification.content.thumbs;
        const thumbUrls = thumbs.reduce((acc: string[], thumb, index) => {
          const size = getThumbSize(thumbs.length, index);
          const media = getThumbMedia(thumb.asset!, {
            width: size.width * DPR,
            height: size.height * DPR,
          });

          if (media?.thumbs?.thumb2) {
            acc.push(media.thumbs.thumb2);
          }

          return acc;
        }, []);

        const chipAvatarsUrls = notification.content.layout.body?.reduce(
          (acc: string[], item) => {
            const avatarUrl = (item as NotificationLayoutItemChip).avatar?.url;
            if (avatarUrl) {
              acc.push(getAvatarResizedURL(avatarUrl, CHIP_AVATAR_SIZE));
            }
            return acc;
          },
          []
        );

        return [...avatarsUrls, ...thumbUrls, chipAvatarsUrls];
      })
      .flat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstNotificationId]);

  useEffect(
    function maybePreloadImages() {
      const images = getImagesToPreload();
      if (!images || images?.length === 0 || imagesToPreload.current === null)
        return;

      for (const src of images) {
        if (typeof src === 'string') {
          const isCached = imagesToPreload.current[src]?.complete;

          if (!isCached) {
            imagesToPreload.current![src] = preloadImage(src);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firstNotificationId]
    /** We use the first notification id to ensure this hook only runs when needed */
  );
}
